import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { some } from 'lodash';
import { BehaviorSubject, Observable, combineLatest, shareReplay } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, takeWhile, tap } from 'rxjs/operators';

import { loadUnitList } from '@dashboards/store/actions/dashboard.actions';
import { selectUnitList, selectUnitListLoading } from '@dashboards/store/selectors/dashboards.selectors';
import { selectSelectedPortfolioId } from '@dashboards/store/selectors/property-selector.selectors';
import { AdministrationAbstract } from '@main-application/administration/abstract/administration-abstract';
import { VENDOR_ROLE_NAME } from '@main-application/administration/constants/vendor-role-name.constant';
import { combineUserWithProperties } from '@main-application/administration/functions/combineUserWithProperties';
import { formatPropertyStructureToTree } from '@main-application/administration/functions/propertyStructureToTree';
import { prepareSelectedUserToPropertyMappings } from '@main-application/administration/functions/userToPropertyMappingHandlers';
import {
  clearUserPreviewData,
  inviteNewResidentUser,
  inviteNewUser,
  loadAllGeneralRoles,
  updateResidentUserData,
  updateUserData,
} from '@main-application/administration/store/actions/administration.actions';
import {
  selectAllPropertiesStructure,
  selectAllUserToPropertyMappings,
  selectAssignUserIntoPropertyInProgress,
  selectAssignUserIntoPropertyStatus,
  selectRemovingPropertyAssignedUserInProgress,
  selectRemovingPropertyAssignedUserStatus,
  selectSaveUserToGeneralRoleInProgress,
  selectSaveUserToGeneralRoleStatus,
  selectUserInviteInProgress,
  selectUserInviteStatus,
  selectUserInvited,
  selectUserUpdateInProgress,
  selectUserUpdateStatus,
} from '@main-application/administration/store/selectors/administration.selectors';
import { UserPreview, UserPreviewFields } from '@main-application/administration/users/config/enums/user-preview';
import { allPortfoliosValue } from '@main-application/application-menu/property-selector/constants';
import { PhoneValidatorService } from '@main-application/shared/services/phone-validator.service';
import { loadAllUserProperties } from '@main-application/store/actions/user.actions';
import { selectNativeLanguagesEnumeration } from '@main-application/store/selectors/enumeration.selectors';
import { selectUserData } from '@main-application/store/selectors/user.selectors';
import { getCompanyList } from '@main-application/turnovers/store/actions/turnovers.actions';
import {
  selectCompanyList,
  selectCompanyListLoading,
} from '@main-application/turnovers/store/selectors/turnovers.selectors';
import { loadAllUserPortfolios } from '@portfolio/store/portfolio.actions';
import { selectPortfolios, selectProperties, selectPropertiesLoading } from '@portfolio/store/portfolio.selectors';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EnumerationItemFields } from '@shared/enums/enumeration-item-fields';
import { EIcon } from '@shared/enums/icon.enum';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { PropertyType } from '@shared/enums/property-type.enum';
import { UpdateStatus } from '@shared/enums/update-status';
import { UserType } from '@shared/enums/user-type';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { getCompanyRadioList } from '@shared/functions/get-company-radio-list.function';
import { getEnumerationRadioListFunction } from '@shared/functions/get-enumeration-radio-list.function';
import { getPortfolioProperties } from '@shared/functions/get-portfolio-properties.function';
import { RestCompanyModel } from '@shared/interfaces/companies.interface';
import { PropertyBasicInfo } from '@shared/interfaces/propertyBasicInfo';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestPropertyModel } from '@shared/interfaces/rest-portfolio-model.interface';
import { TenantData } from '@shared/interfaces/tenant-data';
import { UserData } from '@shared/interfaces/user-data';
import { UserToPropertyMapping } from '@shared/interfaces/user-to-property-mapping';
import {
  NewUserModel,
  UserWithGeneralRole,
  UserWithGeneralRoleAndUnits,
} from '@shared/interfaces/user-with-general-role';
import { UserWithProperties } from '@shared/interfaces/user-with-properties';
import {
  InviteResidentUserModel,
  InviteRestUserModel,
  RestUserModel,
  UpdateResidentUserModel,
  UpdateRestUserModel,
} from '@shared/interfaces/user.interface';
import { PendingService } from '@shared/services/pending.service';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';
import { EmailPatternValidator, correctEmailInput } from '@ui-components/validators/email.validator';
import { RequiredOneOfValidator } from '@ui-components/validators/required-one-of.validator';

export interface UserPreviewModalData {
  vendorId?: number;
  header?: string;
  user: UserWithGeneralRoleAndUnits | NewUserModel;
  preselect: boolean;
  isResident: boolean;
  tenantData?: TenantData;
}

export interface UserPreviewModalResult {
  invitedUser: RestUserModel;
}

const SuperUserRole = 1;
export const residentRoleId = 1000;

@UntilDestroy()
@Component({
  selector: 'app-user-preview-modal',
  templateUrl: './user-preview-modal.component.html',
  styleUrls: ['./user-preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPreviewModalComponent extends AdministrationAbstract implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  isLoading = true;
  EIcon = EIcon;
  EColorPalette = EColorPalette;

  defaultCode = 1; // USA & Canada
  warningMessage = '';
  selectedProperties: PropertyBasicInfo[] = [];
  propertiesToSelect: PropertyBasicInfo[] = [];
  arePropertiesChanged = false;
  root = [];
  isSubmitted = false;
  nativeLanguages: IRadioButtonOption<number>[] = [];
  companyRadioList: IRadioButtonOption<number>[] = [];
  companyListLoading$: Observable<boolean>;

  userToPropertyMapping: UserToPropertyMapping[];
  selectedUsersWithProperties: UserWithProperties[];

  header: string;
  saveButtonText: string;
  isSuperUser: boolean;
  skipDialogClose = false;
  portfolioId!: number;
  properties$: Observable<IRadioButtonOption<number>[]> = this.getProperties();
  properties: RestPropertyModel[] = [];
  units$ = new BehaviorSubject<IRadioButtonOption<number>[] | null>(null);
  selectPropertiesLoading$: Observable<boolean> = this.store.select(selectPropertiesLoading);
  unitListLoading$: Observable<boolean> = this.store.select(selectUnitListLoading);
  saveResidentButtonText = this.data.user ? 'Save' : 'Create Resident';
  federatedIssuerDomains: string[];
  domainExist$ = new BehaviorSubject<boolean>(false);
  phoneNumberInvalid = false;
  editMode = false;
  updatePhoneInputValidation!: Date;
  emailChangedNotified = false;
  phoneChangedNotified = false;
  emailAutoCheck = false;

  constructor(
    protected cdr: ChangeDetectorRef,
    protected store: Store<{}>,
    public dialogRef: MatDialogRef<UserPreviewModalComponent, UserPreviewModalResult>,
    @Inject(MAT_DIALOG_DATA) public data: UserPreviewModalData,
    private formBuilder: FormBuilder,
    private modalsService: ModalsService,
    private readonly pendingService: PendingService,
    private snackbarService: SnackbarService,
    private phoneValidatorService: PhoneValidatorService
  ) {
    super(cdr, store);

    this.editMode = data.user && !data.preselect;
    this.header = data.header || (this.editMode ? 'Edit user' : 'Add new user');
    this.saveButtonText = data.vendorId ? 'Add and Assignee' : this.editMode ? 'Save' : 'Send invitation';
  }

  private userTypesListWithPermissions = [
    {
      value: UserType.Employee,
      label: 'Employee',
      permission: PermissionLevelType.Management_Users,
    },
    {
      value: UserType.Contractor,
      label: 'Vendor',
      permission: PermissionLevelType.Management_Users,
    },
    {
      value: UserType.Resident,
      label: 'Resident',
      permission: PermissionLevelType.Management_Residents,
    },
  ];

  get userTypeListAllowed() {
    const getUserPermissionLevel = this.userData?.permissionLevel;

    if (!getUserPermissionLevel) return [];

    return this.userTypesListWithPermissions.filter(userType =>
      checkPermissionLevel(getUserPermissionLevel, userType.permission)
    );
  }

  get firstName(): FormControl<string> {
    return this.form.get(UserPreviewFields.FirstName) as FormControl<string>;
  }

  get federatedIssuerAssigneeId(): FormControl<string> {
    return this.form.get(UserPreviewFields.FederatedIssuerAssigneeId) as FormControl<string>;
  }

  get lastName(): FormControl<string> {
    return this.form.get(UserPreviewFields.LastName) as FormControl<string>;
  }

  get email(): FormControl<string> {
    return this.form.get(UserPreviewFields.Email) as FormControl<string>;
  }

  get userGroupType(): FormControl<UserType> {
    return this.form.get(UserPreviewFields.UserGroupType) as FormControl<UserType>;
  }

  get propertyId(): FormControl<number> {
    return this.form.get(UserPreviewFields.PropertyId) as FormControl<number>;
  }

  get unitId(): FormControl<number> {
    return this.form.get(UserPreviewFields.UnitId) as FormControl<number>;
  }

  get roleId(): FormControl<number> {
    return this.form.get(UserPreviewFields.RoleId) as FormControl<number>;
  }

  get companyId(): FormControl<number> {
    return this.form.get(UserPreviewFields.CompanyId) as FormControl<number>;
  }

  get phoneNumber(): FormControl {
    return this.form.get(UserPreviewFields.PhoneNumber) as FormControl;
  }

  get phoneNumberAddedAsIdentity(): FormControl {
    return this.form.get(UserPreviewFields.PhoneNumberAddedAsIdentity) as FormControl;
  }

  get emailAddedAsIdentity(): FormControl {
    return this.form.get(UserPreviewFields.EmailAddedAsIdentity) as FormControl;
  }

  get nativeLanguage(): FormControl {
    return this.form.get(UserPreviewFields.NativeLanguage) as FormControl<number>;
  }

  ngOnInit(): void {
    this.pendingService.pending$
      .pipe(
        takeWhile(data => data !== false, true),
        delay(300)
      )
      .subscribe(data => {
        if (data === false) {
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
      .untilDestroyed(this);

    this.store
      .select(selectSelectedPortfolioId)
      .pipe(untilDestroyed(this))
      .subscribe(portfolioId => {
        this.portfolioId = portfolioId;
      })
      .untilDestroyed(this);

    this.initForm();
    this.store.dispatch(getCompanyList());
    this.store.dispatch(loadAllGeneralRoles());
    this.companyListLoading$ = this.store.select(selectCompanyListLoading);
    this.selectAllGeneralRoles().subscribe().untilDestroyed(this);
    this.selectGeneralRoleRadioList().subscribe().untilDestroyed(this);

    if (this.data?.user?.unit?.propertyId)
      this.store.dispatch(loadUnitList({ propertyId: this.data.user.unit.propertyId }));

    this.store
      .select(selectCompanyList)
      .pipe(
        untilDestroyed(this),
        filter((companyList: RestCompanyModel[]) => !!companyList),
        tap((companyList: RestCompanyModel[]) => {
          this.companyRadioList = getCompanyRadioList(companyList).sort((a, b) => a.label.localeCompare(b.label));
          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectNativeLanguagesEnumeration)
      .pipe(
        untilDestroyed(this),
        tap(values => {
          this.nativeLanguages = getEnumerationRadioListFunction(
            values,
            null,
            false,
            EnumerationItemFields.enumerationValue,
            true
          );
          if (this.data.user) {
            this.nativeLanguage.setValue(this.data.user.nativeLanguage);
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
        })
      )
      .subscribe()
      .untilDestroyed(this);

    combineLatest([
      this.store.select(selectAllPropertiesStructure),
      this.store.select(selectAllUserToPropertyMappings),
      this.store.select(selectPortfolios),
    ])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(
          ([allProperties, userToPropertyMapping, portfolios]) =>
            !!allProperties && !!userToPropertyMapping && !!portfolios
        ),
        tap(([allProperties, userToPropertyMapping, portfolios]) => {
          const portfolioIds = new Set(portfolios.map(el => el.id));
          const filteredProperties = allProperties.filter(
            el => el.propertyType !== PropertyType.Property || portfolioIds.has(el.parentId)
          );
          this.userToPropertyMapping = userToPropertyMapping;
          this.root = formatPropertyStructureToTree(filteredProperties);
          const rootNodes = filteredProperties.filter(item => !item.parentId);
          if (this.data.user || this.data.preselect) {
            this.propertiesToSelect = combineUserWithProperties(
              this.data.user?.['previousAddedUserId'] || this.data.user.id,
              filteredProperties,
              userToPropertyMapping
            );
          } else {
            this.propertiesToSelect = rootNodes;
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.userGroupType.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((userGroupType: UserType) => {
          const validators = userGroupType === UserType.Contractor ? [Validators.required] : [];
          if (userGroupType === UserType.Contractor) {
            this.roleId.enable();
            this.roleId.setValidators([Validators.required]);
            this.roleId.updateValueAndValidity();

            this.propertyId.clearValidators();
            this.propertyId.updateValueAndValidity();

            this.unitId.clearValidators();
            this.unitId.updateValueAndValidity();
            const vendroRole = this.userRoles.find(x => x.label === VENDOR_ROLE_NAME);
            if (vendroRole) {
              this.roleId.setValue(vendroRole.value);
            }
          } else if (userGroupType === UserType.Resident) {
            this.roleId.setValue(residentRoleId);
            this.roleId.disable();

            this.propertyId.setValidators([Validators.required]);
            this.propertyId.updateValueAndValidity();

            this.unitId.setValidators([Validators.required]);
            this.unitId.updateValueAndValidity();
          } else {
            this.propertyId.clearValidators();
            this.propertyId.updateValueAndValidity();

            this.unitId.clearValidators();
            this.unitId.updateValueAndValidity();

            this.roleId.enable();
            this.roleId.setValue(null);
            this.roleId.updateValueAndValidity();
          }

          this.companyId.setValidators(validators);
          this.companyId.updateValueAndValidity();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    if (this.editMode) {
      this.inProgress$ = combineLatest([
        this.store.select(selectUserUpdateInProgress),
        this.store.select(selectSaveUserToGeneralRoleInProgress),
        this.store.select(selectAssignUserIntoPropertyInProgress),
        this.store.select(selectRemovingPropertyAssignedUserInProgress),
      ]).pipe(map(operationsInProgress => some(operationsInProgress, Boolean)));

      combineLatest([
        this.store.select(selectUserUpdateStatus),
        this.store.select(selectSaveUserToGeneralRoleStatus),
        this.store.select(selectAssignUserIntoPropertyStatus),
        this.store.select(selectRemovingPropertyAssignedUserStatus),
      ])
        .pipe(
          untilDestroyed(this),
          filter(
            ([
              userUpdateStatus,
              saveUserToGeneralRoleStatus,
              assignUserIntoPropertyStatus,
              removingPropertyAssignedUserStatus,
            ]) =>
              userUpdateStatus === UpdateStatus.UPDATED &&
              (this.data.user.userGroupType === UserType.Resident ||
                (saveUserToGeneralRoleStatus === UpdateStatus.UPDATED &&
                  (this.arePropertiesChanged
                    ? assignUserIntoPropertyStatus === UpdateStatus.UPDATED ||
                      removingPropertyAssignedUserStatus === UpdateStatus.DELETED
                    : true)))
          ),
          tap(() => {
            if (this.arePropertiesChanged && this.userData?.id === this.data.user?.id) {
              this.store.dispatch(loadAllUserProperties());
              this.store.dispatch(loadAllUserPortfolios());
            }

            this.skipDialogClose ? (this.skipDialogClose = false) : this.close(DialogResult.Success);
          })
        )
        .subscribe()
        .untilDestroyed(this);
    } else {
      this.inProgress$ = combineLatest([
        this.store.select(selectUserInviteInProgress),
        this.store.select(selectSaveUserToGeneralRoleInProgress),
        this.store.select(selectAssignUserIntoPropertyInProgress),
      ]).pipe(map(operationsInProgress => some(operationsInProgress, Boolean)));

      combineLatest([
        this.store.select(selectUserInvited),
        this.store.select(selectUserInviteStatus),
        this.store.select(selectSaveUserToGeneralRoleStatus),
        this.store.select(selectAssignUserIntoPropertyStatus),
      ])
        .pipe(
          untilDestroyed(this),
          filter(
            ([invitedUser, userInviteStatus, saveUserToGeneralRoleStatus, assignUserIntoPropertyStatus]) =>
              invitedUser &&
              userInviteStatus === UpdateStatus.OK &&
              (invitedUser.userGroupType === UserType.Resident ||
                (saveUserToGeneralRoleStatus === UpdateStatus.UPDATED &&
                  assignUserIntoPropertyStatus === UpdateStatus.UPDATED))
          )
        )
        .subscribe(([invitedUser]) => {
          this.close(DialogResult.Success, { ...invitedUser, roleId: this.roleId.value });
        })
        .untilDestroyed(this);
    }

    if (this.data.isResident) this.userGroupType.setValue(UserType.Resident);

    if (this.data.vendorId) {
      this.userGroupType.setValue(UserType.Contractor);
      this.companyId.setValue(this.data.vendorId);
    }

    this.roleId.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(roleId => {
        this.isSuperUser = this.checkUserRole(roleId);
      })
      .untilDestroyed(this);

    this.onPropertyControlChange();
    this.onEmailControlChange();
    this.getUnits();
    this.onControlsAddedAsIdentifyChanges();
  }

  save() {
    if (
      this.form.invalid ||
      (this.phoneNumberInvalid && this.phoneNumberAddedAsIdentity.value) ||
      (!this.phoneNumber.value && this.phoneNumberAddedAsIdentity.value) ||
      (!this.email.value && this.emailAddedAsIdentity.value)
    ) {
      this.form.markAllAsTouched();
      this.isSubmitted = true;
      this.cdr.detectChanges();
      return;
    }

    if (
      !this.phoneNumberAddedAsIdentity.value &&
      !this.emailAddedAsIdentity.value &&
      !this.federatedIssuerAssigneeId.value
    ) {
      this.snackbarService.error('Please check Register Email or Register Phone number as identity');
      return;
    }

    const {
      firstName,
      lastName,
      roleId,
      email,
      userGroupType,
      unitId,
      companyId,
      nativeLanguage,
      phoneNumber,
      federatedIssuerAssigneeId,
      phoneNumberAddedAsIdentity,
      emailAddedAsIdentity,
      doNotSendNotifications,
    } = this.form.getRawValue() as UserPreview;

    let userToPropertyRoleList: UserToPropertyMapping[] = [];
    let deleteUserToPropertyMappings: number[] = [];
    let selectedProperty: number[] = [];

    const updateUser: UpdateRestUserModel | UpdateResidentUserModel = {
      id: this.data.user?.id || 0,
      firstName,
      lastName,
      nativeLanguage,
      phoneNumber,
      phoneNumberAddedAsIdentity,
      emailAddedAsIdentity,
      doNotSendNotifications,
    };
    if (this.domainExist$.getValue() && federatedIssuerAssigneeId) {
      updateUser.federatedIssuerAssigneeId = federatedIssuerAssigneeId;
    }
    if (email) updateUser.email = email;

    if (userGroupType !== UserType.Resident) (updateUser as UpdateRestUserModel).companyId = companyId;

    const inviteUser: InviteRestUserModel | InviteResidentUserModel = {
      email,
      firstName,
      lastName,
      userGroupType,
      displayName: `${firstName} ${lastName}`,
      companyId: userGroupType === UserType.Contractor ? companyId : null,
      nativeLanguage,
      phoneNumber,
      phoneNumberAddedAsIdentity,
      emailAddedAsIdentity,
      roleId,
      doNotSendNotifications,
    };

    if (this.domainExist$.getValue() && federatedIssuerAssigneeId) {
      inviteUser.federatedIssuerAssigneeId = federatedIssuerAssigneeId;
    }

    if (userGroupType === UserType.Resident) {
      (inviteUser as InviteResidentUserModel).unitId = unitId;

      if (this.editMode) {
        (updateUser as UpdateResidentUserModel).unitId = unitId;
        this.store.dispatch(
          updateResidentUserData({ user: updateUser as UpdateResidentUserModel, roleId: residentRoleId })
        );
      } else {
        this.store.dispatch(
          inviteNewResidentUser({ user: inviteUser as InviteResidentUserModel, roleId: residentRoleId })
        );
      }

      return;
    }

    const currentUserProperties = this.userToPropertyMapping?.filter(
      item => item.userId === this.data.user?.['previousAddedUserId'] || item.userId === this.data.user?.id
    );
    const currentUserPropertyIds = currentUserProperties
      ? currentUserProperties.map<number>(item => item.propertyId)
      : [];

    if (this.arePropertiesChanged) {
      if (this.editMode) {
        const newUserToPropertyMappings = prepareSelectedUserToPropertyMappings(
          this.data.user as UserWithGeneralRole,
          this.selectedProperties,
          roleId
        );

        const selectedPropertyIds = newUserToPropertyMappings.map<number>(value => value.propertyId);

        const propertyToDelete = this.propertiesToSelect
          .filter(item => !selectedPropertyIds.includes(item.id))
          .map<number>(item => item.id);

        deleteUserToPropertyMappings = currentUserProperties
          .filter(item => propertyToDelete.includes(item.propertyId))
          .map<number>(item => item.id);

        userToPropertyRoleList = newUserToPropertyMappings.filter(
          item => !currentUserPropertyIds.includes(item.propertyId)
        );
      } else {
        selectedProperty = this.selectedProperties.map(item => item.id);
      }
    }

    if (this.editMode) {
      this.store.dispatch(
        updateUserData({ user: updateUser, roleId, userToPropertyRoleList, deleteUserToPropertyMappings })
      );
    } else {
      this.store.dispatch(inviteNewUser({ user: inviteUser, roleId, selectedProperty }));
    }
  }

  close(result: DialogResult = DialogResult.Fail, invitedUser?: RestUserModel) {
    if (result === DialogResult.Fail) {
      this.dialogRef.close();
    } else {
      this.dialogRef.close({
        invitedUser,
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(clearUserPreviewData());
  }

  addNewVendor() {
    this.modalsService
      .openNewVendorModal({})
      .afterClosed()
      .pipe(
        tap(company => {
          if (company) {
            this.companyId.setValue(company.id);
            this.store.dispatch(getCompanyList());
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initForm() {
    if (this.data.tenantData?.federatedIssuer && this.data.tenantData?.federatedIssuerDomains)
      this.federatedIssuerDomains = this.data.tenantData.federatedIssuerDomains.split(',');

    this.form = this.formBuilder.group(
      {
        [UserPreviewFields.FirstName]: [
          this.data.user?.firstName || null,
          [Validators.required, Validators.maxLength(24)],
        ],
        [UserPreviewFields.LastName]: [
          this.data.user?.lastName || null,
          [Validators.required, Validators.maxLength(24)],
        ],
        [UserPreviewFields.Email]: [this.data.user?.email || null, [EmailPatternValidator(true), Validators.email]],
        [UserPreviewFields.UserGroupType]: [this.data.user?.userGroupType || UserType.Employee, [Validators.required]],
        [UserPreviewFields.RoleId]: [this.data.user?.roleId || null, [Validators.required]],
        [UserPreviewFields.CompanyId]: [
          this.data.user?.companyId,
          this.data.user?.userGroupType === UserType.Contractor ? [Validators.required] : [],
        ],
        [UserPreviewFields.PhoneNumber]: [
          this.data.user?.phoneNumber || null,
          [
            this.phoneValidatorService.phoneNumberValidator(
              !this.editMode || (this.editMode && !this.data?.user?.phoneNumberAddedAsIdentity)
            ),
          ],
        ],
        [UserPreviewFields.PhoneNumberAddedAsIdentity]: this.data.user?.phoneNumberAddedAsIdentity || false,
        [UserPreviewFields.EmailAddedAsIdentity]: this.data.user?.emailAddedAsIdentity || false,
        [UserPreviewFields.NativeLanguage]: [this.data.user?.nativeLanguage || 0, []],
        [UserPreviewFields.PropertyId]: [
          this.data.user?.unit?.propertyId || null,
          this.data.user?.userGroupType === UserType.Resident ? [Validators.required] : [],
        ],
        [UserPreviewFields.UnitId]: [
          this.data.user?.unit?.id || null,
          this.data.user?.userGroupType === UserType.Resident ? [Validators.required] : [],
        ],
        [UserPreviewFields.FederatedIssuerAssigneeId]: [this.data.user?.federatedIssuerAssigneeId || null],
        [UserPreviewFields.DoNotSendNotifications]: [this.data.user?.doNotSendNotifications || false],
      },
      { validators: [RequiredOneOfValidator([UserPreviewFields.Email, UserPreviewFields.PhoneNumber])] }
    );

    if (this.data.user?.phoneNumber || this.data.user?.email) {
      const newValidator = RequiredOneOfValidator([], true);
      this.form.setValidators([newValidator]);
      this.form.updateValueAndValidity();
    }

    this.isSuperUser = this.checkUserRole(this.data.user?.roleId);
    correctEmailInput(this.email).subscribe().untilDestroyed(this);

    if (this.data?.tenantData?.federatedIssuer) {
      this.checkForDomain(this.data?.user?.email || '');
    }

    if (this.data.user?.federatedIssuerAssigneeId) {
      this.federatedIssuerAssigneeId.disable();
    }
  }

  selectedRootsChange($event: PropertyBasicInfo[]) {
    this.arePropertiesChanged = true;
    this.selectedProperties = $event;
  }

  private checkUserRole(roleId: number | undefined): boolean {
    return roleId === SuperUserRole; // Did not find role enumeration
  }

  private getProperties(): Observable<IRadioButtonOption<number>[]> {
    return this.store.select(selectProperties).pipe(
      filter((properties: RestPropertyModel[]) => !!properties),
      map(properties => {
        this.properties = properties;
        return getPortfolioProperties(
          properties.filter(p => this.portfolioId === allPortfoliosValue || p.portfolioId === this.portfolioId)
        );
      }),
      shareReplay(),
      untilDestroyed(this)
    );
  }

  private onPropertyControlChange(): void {
    this.propertyId?.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(propertyId => {
        this.unitId.setValue(null);
        if (propertyId) this.store.dispatch(loadUnitList({ propertyId }));
      })
      .untilDestroyed(this);
  }

  private onEmailControlChange(): void {
    this.email?.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(() => !!this.data?.tenantData?.federatedIssuer),
        debounceTime(500)
      )
      .subscribe(value => this.checkForDomain(value))
      .untilDestroyed(this);
  }

  private getUnits(): void {
    this.store
      .select(selectUnitList)
      .pipe(
        map(unitList => {
          return unitList
            .map<IRadioButtonOption<number>>(item => {
              return {
                value: item.id,
                label: item.name,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
        }),
        shareReplay(),
        untilDestroyed(this)
      )
      .subscribe(units => this.units$.next(units))
      .untilDestroyed(this);
  }

  onEmailBlur(): void {
    if (!this.editMode && !this.domainExist$.getValue() && !this.emailAddedAsIdentity.value && !this.emailAutoCheck)
      this.emailAddedAsIdentity.setValue(true);

    this.emailAutoCheck = true;

    if (
      !this.editMode ||
      this.email?.value?.trim() === this.data.user.email?.trim() ||
      !this.data.user?.emailAddedAsIdentity ||
      this.emailChangedNotified
    )
      return;

    this.emailChangedNotified = true;
    this.modalsService
      .openConfirmationModal({
        title: `Are you sure you want to change email?`,
        confirmColor: 'warn',
        cancelLabel: 'Cancel',
        confirmLabel: 'Confirm',
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.save();
          return;
        }
        this.email.setValue(this.data.user?.email || null);
      })
      .untilDestroyed(this);
  }

  onPhoneBlur(): void {
    if (
      !this.editMode ||
      this.phoneNumber?.value?.trim() === this.data.user.phoneNumber?.trim() ||
      !this.data.user?.phoneNumberAddedAsIdentity ||
      this.phoneChangedNotified
    )
      return;

    this.phoneChangedNotified = true;

    this.modalsService
      .openConfirmationModal({
        title: `Are you sure you want to change phone number?`,
        confirmColor: 'warn',
        cancelLabel: 'Cancel',
        confirmLabel: 'Confirm',
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.save();
          return;
        }
        this.phoneNumber.setValue(this.data.user?.phoneNumber || null);
      })
      .untilDestroyed(this);
  }

  phoneInputReady(): void {
    if (this.editMode) {
      this.updatePhoneInputValidation = new Date();
      this.cdr.detectChanges();
    }
  }

  private checkForDomain(value: string): void {
    const domain = value.match(/@(.+)$/)?.[1] || null;
    if (domain && this.federatedIssuerDomains.includes(domain?.trim())) {
      this.form.get(UserPreviewFields.FederatedIssuerAssigneeId).setValidators([Validators.required]);
      this.form.get(UserPreviewFields.FederatedIssuerAssigneeId).updateValueAndValidity();
      this.domainExist$.next(true);
    } else {
      this.form.get(UserPreviewFields.FederatedIssuerAssigneeId).clearValidators();
      this.domainExist$.next(false);
    }
    this.form.get(UserPreviewFields.FederatedIssuerAssigneeId).updateValueAndValidity();
  }

  private onControlsAddedAsIdentifyChanges(): void {
    this.phoneNumberAddedAsIdentity.valueChanges
      .subscribe(val => {
        this.phoneNumber.clearValidators();
        this.phoneNumber.setValidators(this.phoneValidatorService.phoneNumberValidator(!val));
        this.phoneNumber.updateValueAndValidity();
        this.updatePhoneInputValidation = new Date();
      })
      .untilDestroyed(this);
  }

  toggleNotification(val: boolean): void {
    this.form.get(UserPreviewFields.DoNotSendNotifications).setValue(val);
  }
}
